/**
 * Zerlegung EINER Mail in ihre mitgeschickten Iterationen.
 *
 * Ausgangslage (Aufgabe #83): Die Detailansicht zeigte denselben Inhalt zweimal
 * — einmal als vollen Body mit Zitat-Kette hinter dem Knopf „Älteren Verlauf
 * anzeigen", und einmal als eigenständig nachgeladene Thread-Zeilen aus dem
 * Postfach. Entschieden ist: **eine Mail, ein Abruf**. Die früheren Iterationen
 * stehen unter dem neuen Text als einzelne, zugeklappte Einträge — und zwar
 * genau die, die IN DIESER MAIL mitgeschickt wurden.
 *
 * ## Warum am DOM und nicht per Regex am rohen HTML
 *
 * Die Ansicht rendert HTML. Ein Regex-Schnitt am rohen HTML zerreißt Tags und
 * liefert ungültige Segmente. Deshalb: parsen, Grenzen als ELEMENTE bestimmen,
 * am Element-Pfad klonen und beschneiden (``pruneClone``). Das ist die
 * Nachfolge von ``splitQuotedBody`` (bisher in ``Mail.tsx``), nur mit N statt 2
 * Teilen.
 *
 * ## Die Invariante, die alles trägt
 *
 * Gefaltet wird nur, was BEWEISBAR verlustfrei ist:
 * ``nonWs(seg₀) + nonWs(seg₁) + … + nonWs(segₙ) === nonWs(gesamt)``.
 * Schlägt sie fehl, gibt es KEINE Zerlegung — die Mail bleibt ein Stück. Lieber
 * ein Klumpen zu viel als ein verlorener Satz. Es gibt bewusst KEINEN
 * generischen Auffang-Pfad, der „irgendwie" segmentiert.
 *
 * ## Was als Grenze zählt
 *
 * Ein Abschnitt beginnt nur dort, wo ein Kopf mit **erkennbarem Absender**
 * steht. Ohne Absender keine Grenze — der Text bleibt beim vorherigen Segment.
 * Genau diese Forderung hält den Kopf selbst zusammen: in ``Von: … Gesendet: …
 * An: …`` gibt es nach dem ``Von:`` kein zweites, also kann mitten im Kopf
 * keine Grenze entstehen.
 *
 * Marker-Wissen übernommen aus dem produktiven ``html_extractor.py`` des
 * bitop-email-service (Checkout ``119e39c``), das für SF-Import und
 * LLM-Zusammenfassung den neuen Text herausschneidet. Übernommen wurde das
 * WISSEN, nicht die Schnitt-Logik: jene Funktion schneidet beim ERSTEN Treffer
 * ab (``count=1``, ``text[:text.index(…)]``) und wirft den Rest weg. Hier wird
 * nichts weggeworfen — alle Grenzen werden gesucht, auch die INNERHALB der
 * älteren Iterationen.
 */

/** Attribut, mit dem eine gefundene Grenze im DOM markiert wird, damit sie das
 *  Klonen übersteht. Wird aus dem Ergebnis-HTML wieder entfernt. */
const MARK = "data-seg-boundary";

/** Rohzeichen, die ab einer Kandidaten-Position auf einen Kopf geprüft werden.
 *  Wie ``Mail.tsx`` (bisher ``findQuoteBoundary``): großzügig, weil der Wert VOR
 *  dem Zusammenfalten des Leerraums gilt. */
const HEAD_SCAN = 4000;
/**
 * Obergrenze für den Kopfblock. Kein festes Sichtfenster: der Block endet an der
 * ersten Zeile, die keine Kopfzeile mehr ist (s. ``headBlock``). Ein fester Wert
 * war messbar zu klein — bei ``AW: Porto Rafael`` stand ``Betreff:`` erst hinter
 * einer Empfängerliste von über 500 Zeichen, und der Kopf galt als unerkannt.
 */
const HEAD_BLOCK_MAX = 2000;

/** Tags, deren Anfang und Ende eine Zeilen-Grenze sind (wie ``innerText``). */
const BLOCK_TAGS = new Set([
  "ADDRESS", "ARTICLE", "ASIDE", "BLOCKQUOTE", "DD", "DIV", "DL", "DT", "FIELDSET",
  "FIGCAPTION", "FIGURE", "FOOTER", "FORM", "H1", "H2", "H3", "H4", "H5", "H6",
  "HEADER", "HR", "LI", "MAIN", "NAV", "OL", "P", "PRE", "SECTION", "TABLE",
  "TBODY", "TD", "TFOOT", "TH", "THEAD", "TR", "UL",
]);

export const nonWs = (s: string) => s.replace(/\s+/g, "");

// ---- Kopf-Erkennung --------------------------------------------------------
//
// Herkunft jedes Markers ist an der Regel vermerkt. „html_extractor" meint
// C:\Users\w.hansen\wt\sf-admin-email-wt\bitop-email-service\services\
// html_extractor.py (Checkout 119e39c).

/** Absender-Tokens: NUR diese dürfen einen Kopf ERÖFFNEN, weil nur sie einen
 *  Absender tragen. ``Von|From`` aus Mail.tsx:2213-2214, ``De|Da`` zusätzlich
 *  aus html_extractor:40 (``^(From|De|Von|Da):``) und :149. Bewusst NICHT
 *  ``Gesendet|Sent``: die stehen mitten im Kopf, und ein Kopfanfang dort würde
 *  den Kopf selbst zerschneiden. */
const SENDER_TOKEN = /^(?:Von|From|De|Da)\s*:/i;

/** Datums-Tokens. ``Gesendet|Sent`` aus Mail.tsx:2215-2216; ``Datum|Date|
 *  Envoyé|Enviado|Inviato`` aus html_extractor:150. */
const DATE_TOKEN = /\b(?:Gesendet|Sent|Datum|Date|Envoy[ée]|Enviado|Inviato)\s*:/i;

/** Bestätigende Tokens — ein einzelnes „Von:" reicht als Beweis NICHT
 *  (Regel aus Mail.tsx:2211, dort ``tokens >= 2``). ``An|To|Betreff|Subject``
 *  aus Mail.tsx:2217-2220; ``Para|À`` aus html_extractor:151; ``Cc`` ergänzt,
 *  weil Outlook den Kopf oft mit Cc statt Betreff fortsetzt.
 *  Bewusst NICHT übernommen: das blanke ``A:`` aus html_extractor:151 — als
 *  Token viel zu allgemein für deutschen/englischen Fließtext. */
const HEAD_TOKENS = [
  /\bVon\s*:/i, /\bFrom\s*:/i, /\bDe\s*:/i, /\bDa\s*:/i,
  /\bGesendet\s*:/i, /\bSent\s*:/i, /\bDatum\s*:/i, /\bDate\s*:/i,
  /\bEnvoy[ée]\s*:/i, /\bEnviado\s*:/i, /\bInviato\s*:/i,
  /\bAn\s*:/i, /\bTo\s*:/i, /\bPara\s*:/i, /\bÀ\s*:/i,
  /\bCc\s*:/i, /\bBcc\s*:/i, /\bKopie\s*:/i, /\bBlindkopie\s*:/i, /\bCopie\s*:/i, /\bCopia\s*:/i,
  /\bBetreff\s*:/i, /\bSubject\s*:/i, /\bOggetto\s*:/i, /\bAsunto\s*:/i, /\bObjet\s*:/i,
];

/** Dieselben Tokens am ZEILENANFANG — damit erkennt ``headBlock``, wie weit der
 *  zusammenhängende Kopf reicht.
 *
 *  ``Kopie`` ist keine Zierde: Apple Mail schreibt den Weiterleitungs-Kopf als
 *  ``Von: / Datum: / An: / Kopie: / Betreff:``. Ohne dieses Token endete der
 *  Kopfblock an der Kopie-Zeile, der Betreff lag dahinter und galt als fehlend —
 *  gemessen fielen dadurch 35 Mails aus der Zerlegung. */
const HEAD_LINE_TOKEN =
  /^\s*(?:Von|From|De|Da|Gesendet|Sent|Datum|Date|Envoy[ée]|Enviado|Inviato|An|To|Para|À|Cc|Bcc|Kopie|Blindkopie|Copie|Copia|Betreff|Subject|Oggetto|Asunto|Objet)\s*:/i;

/** Trennlinie vor dem Kopf (html_extractor:39 ``^[-=_]{10,}$``, hier ab 2
 *  Zeichen, weil Outlook „-----Ursprüngliche Nachricht-----" schreibt) samt
 *  der optionalen Überschrift. Wird vor der Kopf-Prüfung abgeschnitten. */
const LEAD_IN =
  /^(?:[-=_*]{2,}\s*)?(?:(?:Urspr[üu]ngliche\s+Nachricht|Original\s+Message|Weitergeleitete\s+Nachricht|Forwarded\s+message|Message\s+d'origine|Mensaje\s+original|Messaggio\s+originale)\s*(?:[-=_*]{2,})?\s*)?/i;
/** Dieselbe Überschrift als GANZE Zeile — für die Vorschau, die sie ausblendet. */
const LEAD_IN_ONLY =
  /^[-=_*\s]*(?:Urspr[üu]ngliche\s+Nachricht|Original\s+Message|Weitergeleitete\s+Nachricht|Forwarded\s+message|Message\s+d'origine|Mensaje\s+original|Messaggio\s+originale)[-=_*\s]*$/i;

/** Gmail-/Apple-Mail-Attributionszeile. NICHT aus html_extractor übernommen —
 *  die Datei kennt nur ``class="gmail_signature"`` (:109), nicht die
 *  Attributionszeile. Deutsch: „Am Do., 3. Juli 2026 um 10:12 Uhr schrieb Max
 *  <m@x.de>:" — Absender NACH dem Verb, deshalb sauber zu trennen. */
/**
 * „Ein Zeichen, oder ein Zeilenumbruch, auf den KEINE Leerzeile folgt."
 *
 * Attributionszeilen sind oft weich umbrochen — gemessen etwa
 * ``On Tue, July 22, 2025 2:13 PM, Philip W. <p@x>⏎[p@x]> wrote:``. Ein
 * ``[^\n]``-Muster findet sie nie, und genau diese Mails blieben ungeteilt. Die
 * Leerzeilen-Sperre verhindert zugleich, dass die Suche in den Fließtext
 * darunter ausufert.
 */
const SOFT = "(?:[^\\n]|\\n(?!\\s*\\n))";

const GMAIL_DE = new RegExp(`^Am\\s+(${SOFT}{3,240}?)\\s+schrieb\\s+(${SOFT}{1,240}?)\\s*:`, "i");
/** Englisch/französisch/spanisch/italienisch — Absender VOR dem Verb:
 *  „On Fri, Mar 6, 2026 at 5:16 PM Iskra, Grzegorz <g@bitop.de> wrote:".
 *  Hier steht zwischen Einleitung und Verb BEIDES; die Trennstelle findet
 *  ``splitDateSender``. */
const GMAIL_EN = new RegExp(
  `^(?:On|Le|El|Il)\\s+(${SOFT}{5,300}?)\\s+(?:wrote|a\\s+écrit|escribió|ha\\s+scritto)\\s*:`,
  "i"
);

/** Letzte Uhrzeit („at 5:16 PM", „um 10:12", „07:43:24 PM") — sie beendet die
 *  Datumsangabe. Die Sekunden sind Pflicht-Alternative: ohne sie endete der
 *  Anker bei ``07:43`` und ``:24 PM, Lara Williams …`` wurde zum Absender. */
const TIME_RE = /\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AP]\.?M\.?)?/gi;
/** Jahreszahl als Ersatzanker, wenn keine Uhrzeit dabeisteht. */
const YEAR_RE = /\b(?:19|20)\d{2}\b/g;

/**
 * Trennt „Datum + Absender" einer englischsprachigen Attributionszeile.
 *
 * Ohne Anker lieferte die Regex Unsinn: bei „On Fri, Mar 6, 2026 at 5:16 PM
 * Iskra, Grzegorz <…> wrote:" wurde ``Fri`` zum Datum und der gesamte Rest zum
 * Absender (gemessen an ``Re: connect for ectocare® Spot``). Der verlässliche
 * Anker ist die Uhrzeit, ersatzweise die Jahreszahl: was danach kommt, ist der
 * Absender.
 */
function splitDateSender(mid: string): { date: string | null; sender: string } {
  for (const re of [TIME_RE, YEAR_RE]) {
    re.lastIndex = 0;
    let end = -1;
    for (let m = re.exec(mid); m; m = re.exec(mid)) end = m.index + m[0].length;
    if (end > 0 && end < mid.length) {
      const sender = mid.slice(end).replace(/^[\s,;-]+/, "").trim();
      if (sender) return { date: mid.slice(0, end).trim(), sender };
    }
  }
  const c = mid.lastIndexOf(",");
  if (c > 0 && c < mid.length - 1) {
    const sender = mid.slice(c + 1).trim();
    if (sender) return { date: mid.slice(0, c).trim(), sender };
  }
  return { date: null, sender: mid.trim() };
}

/**
 * Der zusammenhängende Kopfblock ab der Absenderzeile: die erste Zeile plus alle
 * unmittelbar folgenden Zeilen, die selbst mit einem Kopf-Token beginnen. Endet
 * an der ersten Zeile, die keine Kopfzeile mehr ist — das ist der Anfang des
 * zitierten Textes.
 */
function headBlock(collapsed: string): string {
  const lines = collapsed.split("\n");
  const take: string[] = [];
  let len = 0;
  for (const ln of lines) {
    if (take.length > 0 && !HEAD_LINE_TOKEN.test(ln)) break;
    take.push(ln);
    len += ln.length + 1;
    if (len > HEAD_BLOCK_MAX) break;
  }
  return take.join("\n").slice(0, HEAD_BLOCK_MAX);
}

export type QuoteHeadKind = "outlook-header" | "gmail-attr";

export interface QuoteHead {
  kind: QuoteHeadKind;
  /** Absender wie im Kopf geschrieben (Name und/oder Adresse). Nie leer — ohne
   *  Absender entsteht gar keine Grenze. */
  sender: string;
  /** Datum wie im Kopf geschrieben. ``null``, wenn der Kopf keins trägt. */
  date: string | null;
  /** Betreff, falls der Kopf einen trägt (nur ``outlook-header``). */
  subject: string | null;
}

const cut = (s: string, n: number) => (s.length > n ? s.slice(0, n).trim() : s.trim());

/**
 * Wert eines Kopf-Feldes: ab ``label:`` bis zum ZEILENENDE oder zum nächsten
 * Kopf-Token, je nachdem was zuerst kommt.
 *
 * Das Zeilenende ist der eigentliche Anschlag. Ohne ihn frisst das letzte Feld
 * des Kopfs — typisch ``Betreff:``, weil danach kein Token mehr folgt — den
 * gesamten Anfang des zitierten Textes mit; gemessen an ``msg-035`` stand im
 * Betreff die halbe Nachricht samt Signatur. Deshalb trägt ``scanText``
 * Zeilenumbrüche an ``br``- und Block-Grenzen ein statt Leerzeichen.
 */
function field(head: string, label: RegExp): string | null {
  const m = label.exec(head);
  if (!m) return null;
  const rest = head.slice(m.index + m[0].length);
  let end = rest.length;
  const nl = rest.indexOf("\n");
  if (nl >= 0) end = nl;
  for (const t of HEAD_TOKENS) {
    const mm = t.exec(rest);
    if (mm && mm.index < end) end = mm.index;
  }
  const val = rest.slice(0, end).trim().replace(/[;,]$/, "");
  return val ? cut(val, 200) : null;
}

/**
 * Prüft, ob an dieser Textstelle ein Zitat-Kopf beginnt, und liest ihn aus.
 * ``null`` = keine Grenze.
 */
export function matchHead(raw: string): QuoteHead | null {
  // Leerraum zusammenfalten, aber Zeilenumbrüche ERHALTEN — sie begrenzen die
  // Kopf-Felder (s. ``field``).
  const collapsed = raw
    .replace(/[^\S\n]+/g, " ")
    .replace(/ ?\n ?/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
  if (!collapsed) return null;
  // Trennlinie/„Ursprüngliche Nachricht" davor abstreifen (html_extractor:39).
  const lead = LEAD_IN.exec(collapsed);
  const body = lead && lead[0] ? collapsed.slice(lead[0].length).trim() : collapsed;
  if (!body) return null;
  const head = headBlock(body);

  // 1) Outlook-Kopfblock: eröffnendes Absender-Token + mindestens ein zweites
  //    Token (Beweis-Regel aus Mail.tsx:2278-2279).
  if (SENDER_TOKEN.test(head)) {
    const tokens = HEAD_TOKENS.filter((re) => re.test(head)).length;
    if (tokens >= 2) {
      const sender = field(head, /\b(?:Von|From|De|Da)\s*:/i);
      // Der Betreff ist Pflicht, nicht Zierde. Outlook, OWA und Apple Mail
      // schreiben ihn in JEDEN erzeugten Zitat-Kopf; Listen-Mails, die zufällig
      // wie ein Kopf aussehen, haben ihn nicht. Gemessener Anlass: die täglichen
      // Sophos-Quarantäne-Übersichten führen jede isolierte Mail als
      // ``Von: … An: … Datum: … Grund: …`` auf — ohne diese Forderung zerfiel
      // eine solche Übersicht in fünf erfundene „frühere Iterationen".
      const subject = field(head, /\b(?:Betreff|Subject|Oggetto|Asunto|Objet)\s*:/i);
      if (sender && subject) {
        return { kind: "outlook-header", sender, date: field(head, DATE_TOKEN), subject };
      }
    }
  }

  // 2) Attributionszeile (Gmail / Apple Mail). Sie kann weich umbrochen sein
  //    (s. ``SOFT``), deshalb über die ersten Zeilen prüfen statt nur die erste.
  const attr = body.split("\n").slice(0, 3).join("\n");
  // Manche Clients hängen die Adresse ein zweites Mal als Link-Verzierung an
  // (``… <p@x> [p@x]>``). Sie gehört nicht in den Absender.
  const flach = (s: string) =>
    cut(s.replace(/\s+/g, " ").replace(/\s*\[[^\]]*\]\s*>?\s*$/, "").trim(), 200);
  const de = GMAIL_DE.exec(attr);
  if (de) return { kind: "gmail-attr", sender: flach(de[2]), date: flach(de[1]), subject: null };
  const en = GMAIL_EN.exec(attr);
  if (en) {
    const { date, sender } = splitDateSender(en[1].replace(/\s+/g, " "));
    if (sender) return { kind: "gmail-attr", sender: flach(sender), date: date && flach(date), subject: null };
  }

  return null;
}

// ---- DOM-Zerlegung ---------------------------------------------------------

/**
 * Sichtbarer Text des ganzen Baums MIT Zeilen-Separator an ``br``- und
 * Block-Grenzen, plus die Start-Position jedes Elements darin.
 *
 * Warum nicht ``textContent``: Outlook trennt die Zeilen des Zitat-Kopfs oft nur
 * durch ``<br>`` OHNE umgebenden Leerraum. ``textContent`` klebt sie dann
 * zusammen (``…a@b.deGesendet:``), die ``\b``-Wortgrenzen der Kopf-Tokens
 * greifen nicht mehr und der Kopf bleibt unerkannt. ``innerText`` ist keine
 * Option: der Baum ist geparst, nicht gerendert (kein Layout).
 *
 * Ein Durchlauf für das ganze Dokument statt einer Textsammlung pro Element —
 * sonst wäre die Suche quadratisch.
 */
function scanText(root: HTMLElement): { text: string; starts: { el: Element; off: number }[] } {
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT
  );
  const open: Element[] = [];
  const starts: { el: Element; off: number }[] = [];
  let out = "";
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    while (open.length && !open[open.length - 1].contains(n)) {
      if (BLOCK_TAGS.has((open.pop() as Element).tagName)) out += "\n";
    }
    if (n.nodeType === 3 /* Text */) {
      out += n.nodeValue || "";
      continue;
    }
    const e = n as Element;
    open.push(e);
    if (e.tagName === "BR" || BLOCK_TAGS.has(e.tagName)) out += "\n";
    starts.push({ el: e, off: out.length });
  }
  return { text: out, starts };
}

/** Kind-Index-Pfad von ``root`` zu ``el`` (childNodes, inkl. Textknoten). */
function childIndexPath(root: HTMLElement, el: Element): number[] | null {
  const path: number[] = [];
  let cur: Node = el;
  while (cur !== root) {
    const parent: Node | null = cur.parentNode;
    if (!parent) return null;
    const idx = Array.prototype.indexOf.call(parent.childNodes, cur);
    if (idx < 0) return null;
    path.unshift(idx);
    cur = parent;
  }
  return path;
}

/** Klon von ``root``, in dem alles AB (``from``) bzw. VOR der Grenze fehlt. */
function pruneClone(root: HTMLElement, path: number[], keep: "before" | "from"): HTMLElement {
  const clone = root.cloneNode(true) as HTMLElement;
  let node: Node = clone;
  for (let level = 0; level < path.length; level++) {
    const i = path[level];
    const last = level === path.length - 1;
    const kids = Array.prototype.slice.call(node.childNodes) as Node[];
    if (keep === "before") {
      // Alles nach der Grenze weg; auf der letzten Ebene auch die Grenze selbst.
      for (let k = kids.length - 1; k >= (last ? i : i + 1); k--) node.removeChild(kids[k]);
      if (last) break;
      node = node.childNodes[i];
    } else {
      // Alles vor der Grenze weg → die Grenze rutscht auf Index 0.
      for (let k = i - 1; k >= 0; k--) node.removeChild(kids[k]);
      if (last) break;
      node = node.childNodes[0];
    }
  }
  return clone;
}

/** Marker aus dem Ergebnis-HTML entfernen. */
function unmark(el: HTMLElement): string {
  el.querySelectorAll(`[${MARK}]`).forEach((n) => n.removeAttribute(MARK));
  el.removeAttribute(MARK);
  return el.innerHTML;
}

export interface MailSegment {
  /** HTML des Abschnitts, direkt renderbar. */
  html: string;
  /** Erkannter Kopf. ``null`` NUR beim ersten Abschnitt (= der neue Text). */
  head: QuoteHead | null;
  /**
   * Textanfang des Abschnitts OHNE den Kopf — die Vorschau der zugeklappten
   * Zeile. Entsteht hier und nicht in der Ansicht, weil der Baum an dieser
   * Stelle schon geparst vorliegt; die Ansicht müsste jeden Abschnitt erneut
   * parsen.
   */
  preview: string;
}

const PREVIEW_MAX = 160;

/**
 * Vorschautext eines Abschnitts: sein sichtbarer Text, aber ohne den Zitat-Kopf
 * davor. Ohne diesen Abzug stünde in jeder zugeklappten Zeile „Von: … Gesendet:
 * … An: …" statt des Inhalts — also genau das, was die Zeile schon in ihren
 * eigenen Feldern trägt.
 */
function previewOf(el: HTMLElement, head: QuoteHead | null): string {
  // ``scanText`` statt ``textContent``: letzteres kennt keine Block-Grenzen und
  // klebt die Wörter zusammen — gemessen „…haenge die Liste an.Bert
  // MusterMusterabteilung…". In einer einzeiligen Vorschau ist das unlesbar.
  let t = scanText(el).text.replace(/\s+/g, " ").trim();
  if (head) {
    // Den Kopf am zuletzt erkannten Feld abschneiden. Der Betreff steht in allen
    // beobachteten Formaten zuletzt; fehlt er, taugt das Datum als Anker.
    for (const v of [head.subject, head.date, head.sender]) {
      if (!v) continue;
      const i = t.indexOf(v);
      if (i >= 0) { t = t.slice(i + v.length); break; }
    }
    t = t.replace(/^[\s:>|-]+/, "").trim();
  }
  return t.length > PREVIEW_MAX ? `${t.slice(0, PREVIEW_MAX).trimEnd()}…` : t;
}

/** Warum eine Mail ungeteilt blieb — für Messung und Fehlersuche. */
export type SegmentGiveUp =
  | null
  | "kein-dom"
  | "zu-kurz"
  | "parse-fehler"
  | "keine-grenze"
  | "kein-pfad"
  | "invariante-teilschritt"
  | "invariante-gesamt";

export interface SegmentResult {
  segments: MailSegment[];
  /** ``null``, wenn regulär zerlegt wurde. Sonst der Grund für ein Stück. */
  gaveUp: SegmentGiveUp;
  /** Gefundene Grenzen (vor dem Schneiden) — Messgröße. */
  boundaries: number;
}

/**
 * Zerlegt sanitisiertes Body-HTML in ``[neuer Text, Iteration₁, Iteration₂, …]``.
 *
 * Die Reihenfolge ist die des Dokuments und damit **neueste zuerst**: Outlook,
 * OWA und Gmail hängen die ältere Iteration jeweils UNTER die neuere.
 *
 * Ist nichts beweisbar zu trennen, kommt genau ein Segment zurück (``head:
 * null``) — nie eine geratene Zerlegung.
 */
export function splitQuotedSegments(clean: string): SegmentResult {
  const one = (gaveUp: SegmentGiveUp, boundaries = 0): SegmentResult => ({
    segments: [{ html: clean, head: null, preview: "" }],
    gaveUp,
    boundaries,
  });
  if (typeof DOMParser === "undefined") return one("kein-dom");
  if (clean.length < 400) return one("zu-kurz");

  let root: HTMLElement;
  try {
    root = new DOMParser().parseFromString(clean, "text/html").body;
  } catch {
    return one("parse-fehler");
  }

  // 1) Alle Kandidaten-Positionen einsammeln.
  const { text, starts } = scanText(root);
  const heads: { el: Element; head: QuoteHead }[] = [];
  const seen = new Set<string>();
  for (const { el, off } of starts) {
    const slice = text.slice(off, off + HEAD_SCAN);
    const head = matchHead(slice);
    if (!head) continue;
    // Dieselbe Grenze wird von jedem umgebenden Element noch einmal gemeldet
    // (Wrapper-div, p, br davor …). Identität ist der KOPF, nicht das Element:
    // so gewinnt automatisch das äußerste, zuerst gesehene Element.
    const id = `${head.kind}|${head.sender}|${head.date ?? ""}|${head.subject ?? ""}`;
    if (seen.has(id)) continue;
    seen.add(id);
    heads.push({ el, head });
  }
  if (heads.length === 0) return one("keine-grenze");

  // 2) Grenzen markieren, damit sie das Klonen überstehen.
  heads.forEach(({ el }, i) => el.setAttribute(MARK, String(i)));

  // 3) Nacheinander schneiden. Jeder Schritt prüft die Invariante lokal; ein
  //    Fehlschlag beendet die Zerlegung, statt sie zu erraten.
  const parts: HTMLElement[] = [];
  const used: QuoteHead[] = [];
  let cur = root;
  let stopped: SegmentGiveUp = null;
  for (let i = 0; i < heads.length; i++) {
    const b = cur.querySelector(`[${MARK}="${i}"]`);
    if (!b) continue; // Grenze liegt in einem bereits abgetrennten Teil.
    const path = childIndexPath(cur, b);
    if (!path || path.length === 0) {
      stopped = "kein-pfad";
      break;
    }
    const before = pruneClone(cur, path, "before");
    const from = pruneClone(cur, path, "from");
    if (
      nonWs(before.textContent || "") + nonWs(from.textContent || "") !==
      nonWs(cur.textContent || "")
    ) {
      stopped = "invariante-teilschritt";
      break;
    }
    parts.push(before);
    used.push(heads[i].head);
    cur = from;
  }
  parts.push(cur);
  if (parts.length < 2) return one(stopped ?? "keine-grenze", heads.length);

  // 4) Gesamt-Beweis: die Summe ALLER Segmente ist exakt das Original.
  const full = nonWs(root.textContent || "");
  const sum = parts.map((p) => nonWs(p.textContent || "")).join("");
  if (sum !== full) return one("invariante-gesamt", heads.length);

  // 5) Segment k trägt den Kopf der Grenze, an der es beginnt; Segment 0 ist der
  //    neue Text und hat keinen.
  const segments: MailSegment[] = parts.map((p, k) => {
    const head = k === 0 ? null : used[k - 1];
    // Vorschau VOR ``unmark`` lesen — danach ist ``p`` nur noch HTML-String.
    const preview = previewOf(p, head);
    return { html: unmark(p), head, preview };
  });

  // Ein leerer erster Abschnitt entsteht bei der reinen Weiterleitung ohne
  // eigenen Kommentar — dann gibt es schlicht keinen neuen Text. Die Ansicht
  // klappt in dem Fall den ersten verbliebenen Abschnitt auf, damit beim Öffnen
  // Text dasteht statt einer zugeklappten Zeile.
  if (!nonWs(segments[0].html.replace(/<[^>]*>/g, " "))) segments.shift();

  return { segments, gaveUp: stopped, boundaries: heads.length };
}

// ---- Klartext --------------------------------------------------------------
//
// Reine Textmails haben kein ``body_html``; die Ansicht rendert sie als EIN
// ``<pre>``. Gemessen an 469 echten Mails traf das 16 Stück — die längste mit 99
// zitierten Zeilen, also genau der Fall „man müsste ganz weit runterscrollen".
// Ein DOM-Zerleger erreicht sie bauartbedingt nicht, deshalb hier ein zweiter,
// zeilenbasierter — mit DERSELBEN Kopf-Erkennung (``matchHead``, eine Wahrheit)
// und derselben Verlustfreiheits-Regel.
//
// Auf Text ist die Regel sogar beweisbar statt geprüft: geschnitten wird
// ausschließlich an Zeilengrenzen, und keine Zeile wird verworfen. Die
// Zusammenfügung mit ``\n`` ergibt daher zeichengleich das Original — die
// Ansicht prüft das trotzdem nach.

export interface TextSegment {
  text: string;
  head: QuoteHead | null;
  preview: string;
}

/** Zitat-Marker am Zeilenanfang entfernen — NUR zum Erkennen, nie im Ergebnis.
 *  Klartext-Zitate stapeln sie („> > Von:"), und der Kopf steht dahinter. */
const dequote = (s: string) => s.replace(/^\s*(?:>\s?)+/, "");

/**
 * Zerlegt reinen Text in ``[neuer Text, Iteration₁, …]``. Kein erkennbarer Kopf
 * → genau ein Abschnitt, unverändert.
 */
export function splitQuotedTextSegments(text: string): { segments: TextSegment[] } {
  const one = (): { segments: TextSegment[] } => ({
    segments: [{ text, head: null, preview: "" }],
  });
  if (!text || text.length < 200) return one();
  const lines = text.split("\n");

  const bounds: { i: number; head: QuoteHead }[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < lines.length; i++) {
    if (!dequote(lines[i]).trim()) continue;
    // Fenster ab dieser Zeile, ohne Zitat-Marker — so sieht ``matchHead``
    // denselben Kopf wie im HTML-Pfad.
    const probe = lines.slice(i, i + 12).map(dequote).join("\n");
    const head = matchHead(probe);
    if (!head) continue;
    const id = `${head.kind}|${head.sender}|${head.date ?? ""}|${head.subject ?? ""}`;
    if (seen.has(id)) continue;
    seen.add(id);
    bounds.push({ i, head });
  }
  if (bounds.length === 0) return one();

  const cuts = [0, ...bounds.map((b) => b.i), lines.length];
  const segments: TextSegment[] = [];
  for (let k = 0; k + 1 < cuts.length; k++) {
    const teil = lines.slice(cuts[k], cuts[k + 1]);
    if (k === 0 && teil.join("").trim() === "") continue; // kein eigener Text
    const head = k === 0 ? null : bounds[k - 1].head;
    segments.push({ text: teil.join("\n"), head, preview: textPreview(teil, head) });
  }
  if (segments.length < 2) return one();

  // Gegenprobe, obwohl der Bau sie garantiert: nichts verloren, nichts erfunden.
  if (segments.map((s) => s.text).join("\n") !== text) return one();
  return { segments };
}

/** Vorschau eines Textabschnitts: erste inhaltliche Zeile hinter dem Kopf. */
function textPreview(teil: string[], head: QuoteHead | null): string {
  const felder = head ? [head.sender, head.date, head.subject].filter(Boolean) as string[] : [];
  let zeilen = teil.map(dequote);
  // Die Attributionszeile endet auf „… wrote:" / „… schrieb …:" und kann weich
  // umbrochen sein. Alles bis dorthin ist Kopf, nicht Inhalt.
  if (head?.kind === "gmail-attr") {
    const bis = zeilen.slice(0, 3).findIndex((l) => /(?:wrote|schrieb|a\s+écrit|escribió|ha\s+scritto)\s*:/i.test(l));
    if (bis >= 0) zeilen = zeilen.slice(bis + 1);
  }
  const inhalt = zeilen
    .filter((l) => {
      const t = l.trim();
      if (!t) return false;
      if (HEAD_LINE_TOKEN.test(t)) return false;
      // Trennlinien und „-----Ursprüngliche Nachricht-----" sind kein Inhalt;
      // ungefiltert bestand die halbe Vorschau aus Unterstrichen.
      if (/^[-=_*~]{3,}$/.test(t)) return false;
      if (LEAD_IN_ONLY.test(t)) return false;
      // Die Attributionszeile selbst ist kein Inhalt.
      return !felder.some((f) => t.includes(f) && t.length < f.length + 60);
    });
  const s = inhalt.join(" ").replace(/\s+/g, " ").trim();
  return s.length > PREVIEW_MAX ? `${s.slice(0, PREVIEW_MAX).trimEnd()}…` : s;
}
